import { useEffect, useMemo, useState } from "react";
import {
  formatCountdownClock,
  formatRemainingLabel,
  formatTransparencyDate,
  resolveRequestTransparency,
} from "../../utils/requestTransparency";
import "./RequestTransparency.css";

const STATUS_TEXT = {
  done: "تم",
  current: "الآن",
  upcoming: "قادم",
  rejected: "مرفوض",
  skipped: "تم التخطي",
};

function RequestTransparency({
  request,
  compact = false,
  onResubmit,
  resubmitLabel = "عدّل وقدّم تاني",
}) {
  const [now, setNow] = useState(() => new Date());
  const transparency = useMemo(
    () => resolveRequestTransparency(request, now),
    [request, now],
  );

  useEffect(() => {
    if (!transparency?.sla?.applies) return undefined;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [transparency?.sla?.applies]);

  if (!transparency) return null;

  const sla = transparency.sla;
  const overdue = Boolean(sla?.applies && sla.remainingMs <= 0);
  const urgent = Boolean(
    sla?.applies && !sla.paused && sla.remainingMs > 0 && sla.remainingMs <= 2 * 60 * 60 * 1000,
  );
  const visibleTimeline = compact
    ? transparency.timeline.filter((step) => step.status !== "upcoming")
    : transparency.timeline;

  return (
    <section className={`req-transparency${compact ? " is-compact" : ""}`} dir="rtl">
      {transparency.currentHolder ? (
        <p className="req-transparency-holder">
          الطلب حالياً عند <strong>{transparency.currentHolder.roleName}</strong>
        </p>
      ) : null}

      {sla?.applies ? (
        <div
          className={`req-transparency-sla${overdue ? " is-overdue" : ""}${
            sla.paused ? " is-paused" : ""
          }${urgent ? " is-urgent" : ""}`}
        >
          <span className="req-transparency-sla-label">
            {overdue
              ? "تجاوز موعد الرد"
              : sla.paused
                ? sla.label
                : "موعد الرد المتوقع"}
          </span>
          <span className="req-transparency-sla-value">
            {overdue
              ? sla.label
              : sla.paused
                ? formatTransparencyDate(sla.deadline)
                : `${formatRemainingLabel(sla.remainingMs, false)} — ${formatCountdownClock(sla.remainingMs)}`}
          </span>
        </div>
      ) : null}

      {visibleTimeline.length > 0 ? (
        <ol className="req-transparency-timeline">
          {visibleTimeline.map((step) => (
            <li
              key={step.key}
              className={`req-transparency-step is-${step.status}`}
            >
              <span className="req-transparency-dot" aria-hidden="true" />
              <div className="req-transparency-step-body">
                <div className="req-transparency-step-head">
                  <strong>{step.label}</strong>
                  <span>{STATUS_TEXT[step.status] || step.status}</span>
                </div>
                {step.actorName ? (
                  <p className="req-transparency-meta">بواسطة {step.actorName}</p>
                ) : null}
                {step.at ? (
                  <p className="req-transparency-meta">
                    {formatTransparencyDate(step.at)}
                  </p>
                ) : null}
                {step.notes && step.status !== "skipped" ? (
                  <p className="req-transparency-notes">{step.notes}</p>
                ) : null}
                {step.status === "skipped" ? (
                  <p className="req-transparency-notes">{step.notes}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      ) : null}

      {transparency.disbursement ? (
        <p className="req-transparency-disbursement">
          {transparency.disbursement.expectedLabel}
          {transparency.disbursement.assigneeName
            ? ` — ${transparency.disbursement.assigneeName}`
            : ""}
        </p>
      ) : null}

      {transparency.rejection ? (
        <div className="req-transparency-rejection">
          <strong>سبب الرفض</strong>
          <p>{transparency.rejection.reason}</p>
          {transparency.rejection.byName ? (
            <span>من {transparency.rejection.byName}</span>
          ) : null}
        </div>
      ) : null}

      {transparency.canResubmit && onResubmit ? (
        <button
          type="button"
          className="req-transparency-resubmit"
          onClick={() => onResubmit(request)}
        >
          {resubmitLabel}
        </button>
      ) : null}
    </section>
  );
}

export default RequestTransparency;
